import { useEffect, useRef, type ReactNode } from 'react';
import BSideListing from './BSideListing';
import OneStepAheadDetails from './OneStepAheadDetails';
import ToolAccessLabel from './ToolAccessLabel';
import { PREVIEW_INTRO, PROJECT_180 } from '../../core/preview';
import { SCHOOL_PERIODS, type Listing } from '../../core/layouts';
import { PREVIEW_CATALOG } from '../../core/catalog';
import { LISTING_ICONS, ListingStaffBadge, STATUS_LABELS, listingsFromProps } from './layoutListings';
import type { PreviewLayoutProps } from './types';
import './schoolDay.css';

/** E10 arm `school_day`: the catalogue laid out across a teacher's day. */
export default function PreviewSchoolDay(props: PreviewLayoutProps) {
  const { onOsaLearnMore, programActions, marketplaceActions, updatesAction, bSideListing = null, onToolOpen } = props;
  const listings = listingsFromProps(props);
  // Project 180 opens the day: the first card before the bell, the same size as the tools.
  const project = PREVIEW_CATALOG.find(entry => entry.id === PROJECT_180.id);
  const day: Listing[] = [
    ...(project ? [{ entry: project, status: 'coming_soon', period: 'before_bell' } as const] : []),
    ...listings,
  ];
  const page = useRef<HTMLElement>(null);

  // Decoration only: the page warms as the day goes on.
  useEffect(() => {
    const root = page.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target as HTMLElement;
      el.classList.add('is-in');
      if (el.dataset.period) root.dataset.period = el.dataset.period;
    }), { threshold: 0.2, rootMargin: '0px 0px -15% 0px' });
    // Cards hide for the entrance only once something is watching to reveal them.
    root.classList.add('sd-animate');
    root.querySelectorAll('.sd-period').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const [before, after] = PREVIEW_INTRO.headline.split('real work');
  return (
    <main className="preview-page preview-school-day" ref={page} data-period="before_bell">
      <header className="sd-hero">
        {/* The Labs updates signup: a small card in the hero's top-right corner. */}
        {updatesAction && <div className="sd-updates">{updatesAction}</div>}
        <p className="sd-eyebrow">{PREVIEW_INTRO.eyebrow}</p>
        <h1>{after === undefined ? PREVIEW_INTRO.headline : <>{before}<em>real work</em>{after}</>}</h1>
        <p className="sd-lede">{PREVIEW_INTRO.body}</p>
        <div className="sd-hero-cta">
          <a className="sd-btn" href="#school-day">Walk through a school day ↓</a>
          <a className="sd-btn sd-btn-quiet" href="#project-180">{PROJECT_180.name}</a>
        </div>
      </header>

      {day.length > 0 && (
        <div className="sd-schedule" id="school-day">
          {SCHOOL_PERIODS.map(period => {
            const here = day.filter(l => l.period === period.id);
            if (here.length === 0) return null;
            return (
              <section className="sd-period" key={period.id} data-period={period.id} aria-labelledby={`sd-${period.id}`}>
                <div className="sd-stamp"><b>{period.time}</b><span>{period.label}</span></div>
                <div className="sd-dot" aria-hidden="true" />
                <h2 id={`sd-${period.id}`}>{period.title}</h2>
                <p className="sd-why">{period.why}</p>
                <div className="sd-slots">
                  {here.map((listing, i) => (
                    <SchoolDayCard key={listing.entry.id} listing={listing} index={i} props={props}
                      actions={listing.status === 'planned' ? marketplaceActions : listing.status === 'coming_soon' ? programActions : undefined}
                      onOpen={() => onToolOpen(listing.entry.id)}
                      onOsaLearnMore={onOsaLearnMore} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <BSideListing listing={bSideListing} onOpen={() => onToolOpen('b_side')} />

      <p className="preview-footnote">
        240 Labs features may change or disappear. Sign in to your 240 account to get notified and save your choices across devices.
      </p>
    </main>
  );
}

function SchoolDayCard({ listing, index, props, actions, onOpen, onOsaLearnMore }: {
  listing: Listing; index: number; props: PreviewLayoutProps; actions?: ReactNode; onOpen: () => void; onOsaLearnMore?: () => void;
}) {
  const { entry, status, href } = listing;
  const body = (
    <>
      <div className="sd-tool-head">
        <span className="sd-ico" aria-hidden="true">{LISTING_ICONS[entry.id] ?? '✎'}</span>
        <div>
          <h3 id={`${entry.id}-heading`}>{entry.name}</h3>
          <span className={`sd-status sd-status-${status}`}>{STATUS_LABELS[status]}</span>
        </div>
      </div>
      {status === 'open' && entry.destination && <ToolAccessLabel destination={entry.destination} />}
      <ListingStaffBadge id={entry.id} props={props} />
      <p>{entry.description}</p>
    </>
  );
  return (
    <article id={entry.id === PROJECT_180.id ? 'project-180' : undefined} className={`sd-tool${status === 'idea' ? ' sd-tool-idea' : ''}${status === 'coming_soon' ? ' sd-tool-soon' : ''}`} style={{ transitionDelay: `${index * 90}ms` }} aria-labelledby={`${entry.id}-heading`}>
      {body}
      {entry.id === 'one_step_ahead' && <OneStepAheadDetails onOpen={onOsaLearnMore} />}
      {href && <a className="sd-go" href={href} onClick={onOpen}>Open {entry.name}</a>}
      {actions && <div className="sd-actions">{actions}</div>}
    </article>
  );
}
